import { useState } from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, Shield, Globe, Cpu, Users, BarChart3, Send, CheckCircle, ArrowRight } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fadeUp = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0 },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] }
};

const fadeIn = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] }
};

const REASONS = [
  {
    icon: TrendingUp,
    title: '$4.2B+ Client Savings Generated',
    desc: 'Our solutions have delivered measurable, auditable ROI across every deployment. Clients don\'t just adopt our AI — they scale it.',
  },
  {
    icon: Shield,
    title: 'Defense & Government Contracts',
    desc: 'We hold top-level security clearances and long-term contracts with defense and government agencies — providing stable, recurring revenue.',
  },
  {
    icon: Globe,
    title: '47 Countries, 150+ Enterprise Clients',
    desc: 'A global footprint across critical sectors with high switching costs. Our clients include national governments, Fortune 500 energy companies, and leading universities.',
  },
  {
    icon: Cpu,
    title: 'Proprietary AI Technology',
    desc: 'Our custom neural architectures, edge-AI frameworks, and domain-specific models are protected IP that create deep competitive moats.',
  },
  {
    icon: Users,
    title: 'World-Class Team',
    desc: 'PhDs from MIT, former researchers from DeepMind and OpenAI, retired military intelligence officers — a team that cannot be easily replicated.',
  },
  {
    icon: BarChart3,
    title: 'Massive Market Opportunity',
    desc: 'The enterprise AI market is projected to exceed $300B by 2030. We are positioned at the intersection of AI and critical infrastructure — the highest-value segment.',
  },
];

const METRICS = [
  { value: '82%', label: 'YoY Revenue Growth' },
  { value: '$4.2B', label: 'Client Value Created' },
  { value: '97%', label: 'Client Retention Rate' },
  { value: '150+', label: 'Enterprise Clients' },
];

const INVESTMENT_TYPES = [
  'Venture Capital',
  'Private Equity',
  'Strategic / Corporate Investor',
  'Institutional (Pension / Sovereign Wealth)',
  'Family Office',
  'Angel / Individual',
  'Government / Defense Fund',
  'Other'
];

export default function InvestorsPage() {
  const [formData, setFormData] = useState({
    name: '', email: '', company: '', investment_type: '', message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.message) {
      toast.error('Please fill in all required fields.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/investor-inquiry`, formData);
      setSubmitted(true);
      toast.success('Inquiry submitted successfully!');
    } catch (err) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="investors-page">
      {/* Header */}
      <section className="pt-24 pb-4 md:pb-8 relative">
        <div className="absolute inset-0 bg-surface grid-bg" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
          <motion.p {...fadeIn} className="text-xs uppercase tracking-[0.3em] text-brand-accent font-bold mb-4 md:mb-6">
            Investor Relations
          </motion.p>
          <motion.h1
            {...fadeIn}
            transition={{ ...fadeIn.transition, delay: 0.1 }}
            className="text-4xl sm:text-5xl md:text-7xl lg:text-8xl font-heading font-black tracking-tight leading-[0.9] mb-6 md:mb-8"
          >
            Invest in the
            <br />
            <span className="text-gradient-cyan">Future of AI</span>
          </motion.h1>
          <motion.p
            {...fadeIn}
            transition={{ ...fadeIn.transition, delay: 0.2 }}
            className="text-base md:text-lg text-slate-400 font-light max-w-2xl"
          >
            Artificial Mind is transforming how the world&apos;s most critical industries operate. We&apos;re not building consumer apps — we&apos;re building the AI infrastructure that governments and enterprises depend on.
          </motion.p>
        </div>
      </section>

      {/* Key Metrics */}
      <section data-testid="investor-metrics" className="py-14 md:py-20 border-y border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-16">
            {METRICS.map((stat, i) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
              >
                <p className="text-4xl md:text-5xl font-heading font-black stat-value text-white mb-2">
                  {stat.value}
                </p>
                <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-bold">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* TAM SAM SOM */}
      <section data-testid="market-opportunity" className="py-16 md:py-28 section-glow">
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div {...fadeUp} className="mb-12 md:mb-16">
            <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-bold mb-4">Market Opportunity</p>
            <h2 className="text-4xl md:text-5xl font-heading font-bold tracking-tight">
              TAM / SAM / <span className="text-gradient-cyan">SOM</span>
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-16">
            {/* TAM */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="glass-card rounded-lg p-8 lg:p-10 text-center"
            >
              <p className="text-xs uppercase tracking-[0.2em] text-brand-accent font-bold mb-3">Total Addressable Market</p>
              <p className="text-5xl md:text-6xl font-heading font-black text-white mb-2">$2.4T</p>
              <p className="text-sm text-slate-500 font-mono mb-6">by 2032</p>
              <p className="text-slate-400 text-sm leading-relaxed">
                The global AI market across all enterprise applications — software, hardware, and services — is projected to reach $2.4 trillion by 2032, growing at 35%+ CAGR from $244B in 2025.
              </p>
            </motion.div>

            {/* SAM */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="glass-card rounded-lg p-8 lg:p-10 text-center"
            >
              <p className="text-xs uppercase tracking-[0.2em] text-brand-accent font-bold mb-3">Serviceable Addressable Market</p>
              <p className="text-5xl md:text-6xl font-heading font-black text-white mb-2">$155B</p>
              <p className="text-sm text-slate-500 font-mono mb-6">by 2030</p>
              <p className="text-slate-400 text-sm leading-relaxed">
                Enterprise AI solutions in our target verticals — defense ($19B), energy & oil/gas ($15B), government ($28B), transportation ($18B), satellite ($12B), and education ($8B) — represent a $155B opportunity by 2030.
              </p>
            </motion.div>

            {/* SOM */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="glass-card rounded-lg p-8 lg:p-10 text-center"
            >
              <p className="text-xs uppercase tracking-[0.2em] text-brand-accent font-bold mb-3">Serviceable Obtainable Market</p>
              <p className="text-5xl md:text-6xl font-heading font-black text-white mb-2">$8.5B</p>
              <p className="text-sm text-slate-500 font-mono mb-6">by 2030</p>
              <p className="text-slate-400 text-sm leading-relaxed">
                Based on our current growth rate, geographic footprint, security clearances, and enterprise relationships, we project capturing 5.5% of our SAM — translating to $8.5B in annual revenue by 2030.
              </p>
            </motion.div>
          </div>

          {/* Sector Breakdown */}
          <motion.div {...fadeUp} className="glass-card rounded-lg p-8 lg:p-10">
            <h3 className="text-lg font-heading font-semibold mb-6">Sector-Level Market Projections (2030)</h3>
            <div className="space-y-4">
              {[
                { sector: 'Defense & Military AI', size: '$19.3B', cagr: '13.0%', share: '38%', color: 'bg-brand-accent' },
                { sector: 'Government & Civilian AI', size: '$28.0B', cagr: '28.4%', share: '55%', color: 'bg-brand-blue' },
                { sector: 'Energy & Smart Grid', size: '$15.2B', cagr: '24.8%', share: '30%', color: 'bg-emerald-500' },
                { sector: 'Oil & Gas AI', size: '$5.8B', cagr: '12.6%', share: '24%', color: 'bg-amber-500' },
                { sector: 'Railways & Transportation', size: '$18.4B', cagr: '22.1%', share: '36%', color: 'bg-violet-500' },
                { sector: 'Satellite & Space AI', size: '$12.1B', cagr: '18.5%', share: '24%', color: 'bg-sky-500' },
                { sector: 'Education & LMS AI', size: '$8.2B', cagr: '36.2%', share: '16%', color: 'bg-rose-500' },
              ].map((item, i) => (
                <div key={item.sector} className="grid grid-cols-12 gap-4 items-center">
                  <div className="col-span-12 md:col-span-4">
                    <p className="text-sm text-white font-medium">{item.sector}</p>
                  </div>
                  <div className="col-span-4 md:col-span-2">
                    <p className="text-sm font-mono text-brand-accent font-bold">{item.size}</p>
                  </div>
                  <div className="col-span-3 md:col-span-2">
                    <p className="text-xs text-slate-500">{item.cagr} CAGR</p>
                  </div>
                  <div className="col-span-5 md:col-span-4">
                    <div className="h-2 bg-white/5 rounded-full overflow-hidden">
                      <div className={`h-full ${item.color} rounded-full`} style={{ width: item.share }} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <p className="text-xs text-slate-600 mt-6">
              Sources: Grand View Research, Mordor Intelligence, MarketsandMarkets, Precedence Research (2025)
            </p>
          </motion.div>
        </div>
      </section>

      {/* Why Invest */}
      <section data-testid="why-invest" className="py-16 md:py-28 section-glow">
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div {...fadeUp} className="mb-12 md:mb-16">
            <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-bold mb-4">The Opportunity</p>
            <h2 className="text-4xl md:text-5xl font-heading font-bold tracking-tight">
              Why <span className="text-gradient-cyan">Artificial Mind</span>
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
            {REASONS.map((reason, i) => (
              <motion.div
                key={reason.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className={`glass-card rounded-lg p-8 lg:p-10 group ${
                  i % 3 === 0 ? 'md:col-span-8' : 'md:col-span-4'
                }`}
              >
                <reason.icon className="w-8 h-8 text-brand-accent mb-6 opacity-70 group-hover:opacity-100 transition-opacity duration-300" />
                <h3 className="text-lg md:text-xl font-heading font-semibold mb-3">{reason.title}</h3>
                <p className="text-slate-400 text-sm leading-relaxed">{reason.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Thesis */}
      <section className="py-16 md:py-24 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
            <motion.div {...fadeUp} className="lg:col-span-7">
              <p className="text-xs uppercase tracking-[0.2em] text-brand-accent font-bold mb-4">Investment Thesis</p>
              <h2 className="text-3xl md:text-4xl font-heading font-bold tracking-tight mb-8">
                Critical infrastructure needs AI that works. We build it.
              </h2>
              <div className="space-y-6 text-slate-400 text-sm leading-relaxed">
                <p>
                  The next decade will see AI become mandatory for national defense, energy grids, transportation networks, and education systems. Governments and enterprises are not looking for chatbots — they need production-grade, secure, domain-specific AI that operates in the harshest environments.
                </p>
                <p>
                  Artificial Mind is one of the few companies in the world with the security clearances, domain expertise, and technical depth to serve these markets. Our contracts are long-term, our switching costs are high, and our technology moat deepens with every deployment.
                </p>
                <p>
                  We are raising capital to expand into new geographies, accelerate R&D on next-generation models, and double our engineering team to meet surging demand.
                </p>
              </div>
            </motion.div>

            <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.1 }} className="lg:col-span-5">
              <div className="glass-card rounded-lg p-8 space-y-6">
                <h3 className="text-lg font-heading font-semibold mb-2">Use of Funds</h3>
                {[
                  { label: 'R&D & New AI Models', pct: '40%' },
                  { label: 'Geographic Expansion', pct: '25%' },
                  { label: 'Team Growth', pct: '20%' },
                  { label: 'Infrastructure & Security', pct: '15%' },
                ].map((item) => (
                  <div key={item.label}>
                    <div className="flex justify-between mb-2">
                      <span className="text-sm text-slate-400">{item.label}</span>
                      <span className="text-sm font-mono text-white font-bold">{item.pct}</span>
                    </div>
                    <div className="h-1 bg-white/5 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-brand-accent rounded-full"
                        style={{ width: item.pct }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Future Potential */}
      <section data-testid="future-potential" className="py-16 md:py-24 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div {...fadeUp} className="mb-12 md:mb-16">
            <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-bold mb-4">Looking Ahead</p>
            <h2 className="text-4xl md:text-5xl font-heading font-bold tracking-tight">
              Future <span className="text-gradient-cyan">Potential</span>
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              {
                title: 'Autonomous Defense Systems',
                desc: 'The shift from human-operated to AI-autonomous defense systems is accelerating globally. Nations are mandating AI-first military strategies. Our drone surveillance and maritime monitoring tech positions us at the center of this $34B autonomous defense market.',
                tag: '2025-2028'
              },
              {
                title: 'National AI Infrastructure',
                desc: 'Governments worldwide are building sovereign AI infrastructure. Our government contracts and security clearances make us a natural partner for national AI platforms — a $50B+ opportunity as countries seek technological independence.',
                tag: '2026-2030'
              },
              {
                title: 'AI-Native Education',
                desc: 'The $8B education AI market is set to explode as LLMs transform assessment, personalized learning, and research. Our LMS platform with automated scoring and feedback is already deployed across universities — early mover advantage in a market growing at 36% CAGR.',
                tag: '2025-2030'
              },
              {
                title: 'Energy Transition Intelligence',
                desc: 'The global energy transition to renewables requires AI-optimized grids, demand forecasting, and carbon monitoring. As traditional energy companies transform, our smart grid and predictive analytics solutions become critical infrastructure.',
                tag: '2025-2032'
              },
              {
                title: 'Space & Satellite AI',
                desc: 'With 10,000+ new satellites launching by 2030, the need for AI-powered imagery analysis, anomaly detection, and orbital management is surging. Our satellite AI platform already processes 4TB daily — built for the scale that\'s coming.',
                tag: '2025-2030'
              },
              {
                title: 'AI Regulation & Compliance',
                desc: 'As AI regulations tighten globally, enterprises need compliant, auditable, explainable AI systems. Our defense-grade AI is built with compliance by design — giving us a massive advantage as regulation becomes mandatory across all sectors.',
                tag: '2025-2028'
              },
            ].map((item, i) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="glass-card rounded-lg p-8 group"
              >
                <span className="text-xs font-mono text-brand-accent border border-brand-accent/30 px-2 py-1 mb-4 inline-block">{item.tag}</span>
                <h3 className="text-lg font-heading font-semibold mb-3">{item.title}</h3>
                <p className="text-slate-400 text-sm leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Investor Contact Form */}
      <section data-testid="investor-form-section" className="py-16 md:py-24 border-t border-white/5 section-glow">
        <div className="relative z-10 max-w-3xl mx-auto px-6 lg:px-8">
          <motion.div {...fadeUp} className="mb-10 md:mb-14 text-center">
            <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-bold mb-4">Get in Touch</p>
            <h2 className="text-3xl md:text-4xl font-heading font-bold tracking-tight mb-4">
              Investor <span className="text-gradient-cyan">Inquiry</span>
            </h2>
            <p className="text-slate-400 text-sm max-w-md mx-auto">
              Interested in learning more? Submit your details and our investor relations team will reach out within 48 hours.
            </p>
          </motion.div>

          {submitted ? (
            <motion.div {...fadeUp} data-testid="investor-success" className="glass-card rounded-lg p-12 text-center">
              <CheckCircle className="w-16 h-16 text-emerald-500 mx-auto mb-6" />
              <h3 className="text-2xl font-heading font-bold mb-4">Inquiry Received</h3>
              <p className="text-slate-400 text-sm">
                Our investor relations team will review your inquiry and respond within 48 hours.
              </p>
              <button
                onClick={() => { setSubmitted(false); setFormData({ name: '', email: '', company: '', investment_type: '', message: '' }); }}
                data-testid="investor-reset"
                className="mt-8 text-sm text-slate-400 hover:text-white uppercase tracking-widest underline underline-offset-8 transition-colors duration-200"
              >
                Submit Another Inquiry
              </button>
            </motion.div>
          ) : (
            <motion.form {...fadeUp} onSubmit={handleSubmit} data-testid="investor-form" className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-xs uppercase tracking-[0.15em] text-slate-500 font-bold mb-2">Name *</label>
                  <input
                    type="text" name="name" value={formData.name} onChange={handleChange}
                    data-testid="investor-name"
                    className="input-dark w-full h-14 px-4 text-sm" placeholder="Your full name" required
                  />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-[0.15em] text-slate-500 font-bold mb-2">Email *</label>
                  <input
                    type="email" name="email" value={formData.email} onChange={handleChange}
                    data-testid="investor-email"
                    className="input-dark w-full h-14 px-4 text-sm" placeholder="your@email.com" required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-xs uppercase tracking-[0.15em] text-slate-500 font-bold mb-2">Firm / Organization</label>
                  <input
                    type="text" name="company" value={formData.company} onChange={handleChange}
                    data-testid="investor-company"
                    className="input-dark w-full h-14 px-4 text-sm" placeholder="Your firm name"
                  />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-[0.15em] text-slate-500 font-bold mb-2">Investor Type</label>
                  <select
                    name="investment_type" value={formData.investment_type} onChange={handleChange}
                    data-testid="investor-type"
                    className="input-dark w-full h-14 px-4 text-sm appearance-none cursor-pointer"
                  >
                    <option value="" className="bg-surface-paper">Select type</option>
                    {INVESTMENT_TYPES.map((t) => (
                      <option key={t} value={t} className="bg-surface-paper">{t}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs uppercase tracking-[0.15em] text-slate-500 font-bold mb-2">Message *</label>
                <textarea
                  name="message" value={formData.message} onChange={handleChange}
                  data-testid="investor-message"
                  className="input-dark w-full px-4 py-4 text-sm min-h-[140px] resize-none"
                  placeholder="Tell us about your interest in Artificial Mind..."
                  required
                />
              </div>

              <button
                type="submit" disabled={submitting}
                data-testid="investor-submit"
                className="inline-flex items-center justify-center gap-2 bg-white text-black font-bold text-sm uppercase tracking-wider px-10 py-5 hover:bg-slate-200 transition-colors duration-200 disabled:opacity-50 w-full sm:w-auto"
              >
                {submitting ? 'Submitting...' : 'Submit Inquiry'} <Send size={16} />
              </button>
            </motion.form>
          )}
        </div>
      </section>
    </div>
  );
}
